import type { GetServerSidePropsContext } from 'next'

import { AUTH_API_TIMEOUT, DEFAULT_ERROR_MESSAGE } from '../constants'

type RequestOptions = {
  method?: 'GET' | 'POST'
  body?: Record<string, unknown>
  headers?: Record<string, string>
  ctx?: GetServerSidePropsContext
  redirect?: 'follow' | 'error' | 'manual'
  signal?: AbortSignal
}

function isTimeoutOrNetworkError(err: unknown): boolean {
  if (err instanceof Error) {
    if (err.name === 'AbortError') return true
    if (/timeout|aborted|signal|network|failed to fetch/i.test(err.message)) return true
  }
  return false
}

export async function makeRequest(
  url: string,
  options: RequestOptions = {},
  getHeaders?: (opts: {
    ctx?: GetServerSidePropsContext
    url?: string
  }) => Record<string, string> | Promise<Record<string, string>>
) {
  try {
    const headers = {
      ...(getHeaders ? await getHeaders({ ctx: options.ctx, url }) : {}),
      ...(options.headers ?? {}),
    }
    return await fetch(url, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'include',
      redirect: options.redirect ?? 'manual',
      signal: AbortSignal.timeout(AUTH_API_TIMEOUT),
    })
  } catch (err) {
    if (isTimeoutOrNetworkError(err)) {
      throw new Error(DEFAULT_ERROR_MESSAGE)
    }
    throw err
  }
}

export async function parseJsonResponse<T>(
  response: Response
): Promise<{ response: Response; data: T | undefined }> {
  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    try {
      const data = await response.json()
      return { response, data }
    } catch (err) {
      console.error('[API] Failed to parse JSON response:', err)
    }
  }
  return { response, data: undefined }
}
